import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const POLL_INTERVAL = 30000;

/**
 * Banner øverst på siden når en nyere versjon er deployet.
 *
 * version.json deployes sammen med bundlen, mens __APP_BUILD_ID__ er bakt inn
 * i bundlen som kjører. Er de ulike, sitter søkeren på en utdatert versjon der
 * chunk-filene kan være slettet av den nye deployen. Samme mekanisme som
 * lyfti-admin, men et banner i stedet for en toast.
 *
 * Bevisst ikke automatisk reload: søkeren kan stå midt i et skjema. Banneret
 * blir stående til søkeren velger Oppdater eller lukker det — da varsles
 * bare en enda nyere versjon igjen.
 */
const NewVersionBanner = () => {
    const { t } = useTranslation();
    const [latestVersion, setLatestVersion] = useState<string | null>(null);
    const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);

    useEffect(() => {
        // version.json finnes bare i produksjonsbygg
        if (!import.meta.env.PROD) return;

        let isMounted = true;

        const fetchVersion = async () => {
            try {
                const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
                if (!response.ok) return;

                const data = await response.json();
                if (isMounted && typeof data?.build === 'string') {
                    setLatestVersion(data.build);
                }
            } catch {
                // En feilet versjonssjekk skal aldri påvirke appen — nytt forsøk ved neste intervall
            }
        };

        fetchVersion();
        const interval = setInterval(fetchVersion, POLL_INTERVAL);

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, []);

    if (!latestVersion || latestVersion === __APP_BUILD_ID__ || latestVersion === dismissedVersion) {
        return null;
    }

    return (
        <div className="alert alert-info rounded-0 border-0 border-bottom mb-0 py-2 sticky-top no-print" role="status">
            <div className="container d-flex flex-wrap align-items-center justify-content-center gap-2 gap-md-3">
                <span>{t('new_version_available')}</span>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => window.location.reload()}>
                        {t('new_version_update')}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setDismissedVersion(latestVersion)}>
                        {t('close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewVersionBanner;
